// Sizes of clothing (docs/plans/sizes-colours.md): ready size runs to tap, the shop's own
// runs gathered from its groups, and the order sizes are shown in.

export interface SizeRun {
  name: string
  sizes: readonly string[]
}

export const SIZE_RUNS: readonly SizeRun[] = [
  { name: 'Shirts & kurtas', sizes: ['S', 'M', 'L', 'XL', 'XXL'] },
  { name: 'Pants', sizes: ['28', '30', '32', '34', '36', '38', '40'] },
  { name: 'Shoes', sizes: ['5', '6', '7', '8', '9', '10', '11'] },
  { name: 'Kids', sizes: ['2', '4', '6', '8', '10', '12', '14'] },
  { name: 'Free size', sizes: ['Free size'] },
]

/** Letter sizes from smallest to largest, for sorting typed sizes. */
const LETTERS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL']

/** "S to XXL", "28 to 40" or the one size. */
export function runLabel(sizes: readonly string[]): string {
  if (sizes.length === 0) return ''
  if (sizes.length === 1) return sizes[0]!
  return `${sizes[0]} – ${sizes[sizes.length - 1]}`
}

/**
 * Sizes in a sensible order: letter sizes by size, then numbers, then the rest as typed.
 * Repeats (any case) are dropped.
 */
export function sortSizes(sizes: readonly string[]): string[] {
  const seen = new Set<string>()
  const unique = sizes.filter((s) => {
    const k = s.trim().toLowerCase()
    if (!k || seen.has(k)) return false
    seen.add(k)
    return true
  })
  const rank = (s: string): [number, number] => {
    const letter = LETTERS.indexOf(s.trim().toUpperCase())
    if (letter >= 0) return [0, letter]
    const n = Number(s.trim())
    if (Number.isFinite(n) && s.trim() !== '') return [1, n]
    return [2, 0]
  }
  return unique
    .map((s, i) => ({ s, i, r: rank(s) }))
    .sort((a, b) => a.r[0] - b.r[0] || a.r[1] - b.r[1] || a.i - b.i)
    .map((x) => x.s)
}

/**
 * The shop's own size lists, from its groups' options (most used first), leaving out
 * ones that are the same as a ready run.
 */
export function shopRuns(lists: readonly (readonly string[])[]): SizeRun[] {
  const key = (sizes: readonly string[]) => sizes.map((s) => s.trim().toLowerCase()).join('|')
  const ready = new Set(SIZE_RUNS.map((r) => key(r.sizes)))
  const counts = new Map<string, { sizes: readonly string[]; uses: number }>()
  for (const sizes of lists) {
    if (sizes.length === 0) continue
    const k = key(sizes)
    if (ready.has(k)) continue
    const entry = counts.get(k) ?? { sizes, uses: 0 }
    entry.uses += 1
    counts.set(k, entry)
  }
  return [...counts.values()]
    .sort((a, b) => b.uses - a.uses)
    .slice(0, 4)
    .map((e) => ({ name: 'Yours', sizes: e.sizes }))
}
